import definition from '../../server/internal/specialvillages/creative_world.json';

export const CREATIVE_WORLD = definition;
export type CreativeBiomeWeights = readonly [
  number,
  number,
  number,
  number,
  number,
  number,
  ...number[],
];

// Integer hashing keeps the seeded noise identical in Go and JavaScript, including
// negative coordinates. No chunk-local state: adjacent meshes share their borders.
function lattice(x: number, z: number, salt: number): number {
  let hash =
    definition.seed ^
    Math.imul(x, 374761393) ^
    Math.imul(z, 668265263) ^
    Math.imul(salt, 1442695041);
  hash = Math.imul(hash ^ (hash >>> 13), 1274126177);
  return ((hash ^ (hash >>> 16)) >>> 0) / 4294967296;
}

function smooth(value: number): number {
  const t = Math.max(0, Math.min(1, value));
  return t * t * (3 - 2 * t);
}

function noise(x: number, z: number, salt: number): number {
  const ix = Math.floor(x),
    iz = Math.floor(z);
  const tx = smooth(x - ix),
    tz = smooth(z - iz);
  const a = lattice(ix, iz, salt),
    b = lattice(ix + 1, iz, salt);
  const c = lattice(ix, iz + 1, salt),
    d = lattice(ix + 1, iz + 1, salt);
  return (a + (b - a) * tx) * (1 - tz) + (c + (d - c) * tx) * tz;
}

/** Disabled entries retain shader slots but receive no weight. Broad, warped regions; mirrored by Go and verified against golden samples. */
export function creativeBiomeWeights(x: number, z: number, height = 7): CreativeBiomeWeights {
  const { scale, warp, blend } = definition.biomes;
  const wx = x + warp * (2 * noise(x / scale, z / scale, 1) - 1);
  const wz = z + warp * (2 * noise(x / scale, z / scale, 2) - 1);
  const distances = definition.regions.map((region) =>
    region.theme === 12 || region.disabled ? Infinity : Math.hypot(wx - region.x, wz - region.z),
  );
  const nearest = Math.min(...distances);
  const raw = distances.map((distance) => 1 - smooth((distance - nearest) / blend));
  const total = raw.reduce((sum, weight) => sum + weight, 0);
  const weights = raw.map((weight) => weight / total);
  const submerged = smooth((-height - 4) / 16);
  return weights.map(
    (weight, index) =>
      weight * (1 - submerged) + (definition.regions[index]?.theme === 12 ? submerged : 0),
  ) as unknown as CreativeBiomeWeights;
}

export function creativeThemeAt(x: number, z: number, sample = 0.5, height = 7): number {
  const weights = creativeBiomeWeights(x, z, height);
  for (let i = 0; i < weights.length; i++) {
    const weight = weights[i] ?? 0;
    sample -= weight;
    if (weight > 0 && sample <= 0) return definition.regions[i]?.theme ?? 2;
  }
  return 2;
}
